
import { defineComponent, PropType } from 'vue';
import Overlay from '@/components/Overlay.vue';
import NoAccess from '@/components/NoAccess.vue';
import VChart from 'vue-echarts';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { IRealizedBudget } from '@/modules/dashboardBrandManagement/types';
import { use } from 'echarts/core';
import { CanvasRenderer, SVGRenderer } from 'echarts/renderers';
import { seriesChart } from '@/common/helpers/RealizedBudget';
import filters from '@/common/filters';

use([
  BarChart,
  LineChart,
  CanvasRenderer,
  SVGRenderer,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
]);

export default defineComponent({
  name: 'DashboardRealizedBudget',

  components: {
    Overlay,
    VChart,
    NoAccess,
  },

  props: {
    realizedBudget: {
      type: Array as PropType<IRealizedBudget[]>,
      default: () => [],
    },
    channelName: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    access: {
      type: Boolean,
      default: false,
    },
    salesChannelName: {
      type: String,
      default: 'ECOMM',
    },
  },

  data() {
    return {
      brands: [] as any,
      options: {
        grid: {
          right: '1',
          left: '80',
          top: 55,
          bottom: 48,
        },
        xAxis: {
          axisTick: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#4A4B4D',
            },
          },
          type: 'category',
          axisLabel: {
            formatter: () => '',
          },
        },
        yAxis: {
          axisTick: {
            show: false,
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#4A4B4D',
            },
          },
          splitLine: {
            show: false,
          },
          type: 'value',
          axisLabel: {
            formatter: '{value}',
            fontSize: filters.isMobile() ? 9 : 12,
          },
        },
        series: [],
      },
    };
  },
  methods: {
    gerarSeries() {
      this.realizedBudget.forEach((element) => {
        this.brands.push(element.brand.toLowerCase().split(' ').join(''));
      });

      let series = [
        {
          name: 'chartName',
          cursor: 'normal',
          type: 'bar',
          silent: true,
          barWidth: this.$filters.isMobile() ? '25px' : '35px',
          z: 1,
          stack: 'Acessórios',
          data: seriesChart(this.realizedBudget).real,
          label: {
            show: true,
            position: 'bottom',
            color: '#fff',
            formatter: (value: any) => {
              return `{${this.brands[value.dataIndex]}|}`;
            },
            rich: {
              value: {
                lineHeight: 0,
                align: 'center',
              },
              fila: {
                height: this.$filters.isMobile() ? 10 : 19,
                lineHeight: 40,
                align: 'center',
                backgroundColor: {
                  image: this.icons.fila,
                },
              },
              filaacessorios: {
                height: this.$filters.isMobile() ? 14 : 19,
                lineHeight: 40,
                backgroundColor: {
                  image: this.icons.fila,
                },
              },
              umbro: {
                height: this.$filters.isMobile() ? 16 : 22,
                lineHeight: 40,
                align: 'center',
                backgroundColor: {
                  image: this.icons.umbro,
                },
              },
              newbalance: {
                height: this.$filters.isMobile() ? 16 : 19,
                lineHeight: 40,
                align: 'center',
                backgroundColor: {
                  image: this.icons.newbalance,
                },
              },
            },
          },
          itemStyle: {
            color: '#00AFD7',
          },
        },
        {
          name: 'Faltante',
          cursor: 'normal',
          type: 'bar',
          silent: true,
          stack: 'Acessórios',
          z: 1,
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: (value) =>
              seriesChart(this.realizedBudget).positive[value.dataIndex],
          },
          label: {
            show: true,
            position: 'top',
            formatter: (value: any) => {
              return `{saldo${
                seriesChart(this.realizedBudget).positiveColor[value.dataIndex]
              }|${
                seriesChart(this.realizedBudget).balanceMask[value.dataIndex]
              }} \n {realizado|${
                seriesChart(this.realizedBudget).realMask[value.dataIndex]
              }}`;
            },
            rich: {
              space: {},
              realizado: {
                color: '#00AFD7',
                fontSize: this.$filters.isMobile() ? 9 : 10,
              },
              saldoNegative: {
                color: '#E70024',
                padding: 3,
                fontSize: this.$filters.isMobile() ? 9 : 10,
              },
              saldoPositive: {
                padding: 3,
                color: '#219653',
                fontSize: this.$filters.isMobile() ? 9 : 10,
              },
            },
          },
          data: seriesChart(this.realizedBudget).balance,
        },
        {
          name: 'Direct',
          type: 'line',
          step: 'middle',
          symbol: 'line',
          stack: 'a',
          z: 0,
          data: seriesChart(this.realizedBudget).budgeted,
          itemStyle: {
            color: '#97928F',
            opacity: '0.25',
          },
          areaStyle: {
            opacity: '0.25',
          },
        },
      ];
      (this.$refs['chart'] as any).setOption({ series });
    },
  },

  watch: {
    realizedBudget: {
      handler() {
        this.gerarSeries();
      },
      deep: true,
    },
  },
  setup() {
    const icons = {
      fila: require('@/assets/images/fila-logo.svg'),
      umbro: require('@/assets/images/umbro-logo.svg'),
      newbalance: require('@/assets/images/newbalance.png'),
    };

    return { icons };
  },
});
